import { FinancialTool } from "../../types";

export const mortgageNewTools: FinancialTool[] = [
  {
    id: "rent-vs-buy",
    name: "Rent vs Buy Calculator",
    description: "Compare the total lifetime cost of renting versus purchasing a home, factoring in interest, appreciation, and maintenance.",
    category: "loans",
    inputs: [
      { id: "monthlyRent", label: "Current Monthly Rent ($)", type: "currency", defaultValue: 2200, min: 500, max: 10000, step: 100 },
      { id: "rentIncrease", label: "Annual Rent Increase (%)", type: "percentage", defaultValue: 3.5, min: 0, max: 15, step: 0.1 },
      { id: "homePrice", label: "Target Home Purchase Price ($)", type: "currency", defaultValue: 420000, min: 50000, max: 2500000, step: 5000 },
      { id: "downPayment", label: "Down Payment ($)", type: "currency", defaultValue: 84000, min: 0, max: 1000000, step: 2000 },
      { id: "interestRate", label: "Annual Interest Rate (%)", type: "percentage", defaultValue: 6.8, min: 1, max: 15, step: 0.1 },
      { id: "termYears", label: "Mortgage Term (Years)", type: "number", defaultValue: 30, min: 10, max: 30, step: 5 },
      { id: "appreciationRate", label: "Annual Home Appreciation (%)", type: "percentage", defaultValue: 3.8, min: -2, max: 12, step: 0.1 },
      { id: "maintenanceCost", label: "Annual Maintenance & Insurance (%)", type: "percentage", defaultValue: 1.5, min: 0, max: 5, step: 0.1 }
    ],
    calculate: (inputs) => {
      const monthlyRent = inputs.monthlyRent || 2200;
      const rentIncrease = (inputs.rentIncrease || 3.5) / 100;
      const homePrice = inputs.homePrice || 420000;
      const downPayment = inputs.downPayment || 84000;
      const interestRate = (inputs.interestRate || 6.8) / 100;
      const termYears = inputs.termYears || 30;
      const appreciationRate = (inputs.appreciationRate || 3.8) / 100;
      const maintenancePercent = (inputs.maintenanceCost || 1.5) / 100;

      const yearsToCompare = 10; // Standard 10-year lock-in comparison
      const months = yearsToCompare * 12;

      // 1. Calculate Renting Cost
      let currentRent = monthlyRent;
      let totalRentPaid = 0;
      for (let y = 0; y < yearsToCompare; y++) {
        totalRentPaid += currentRent * 12;
        currentRent *= (1 + rentIncrease);
      }

      // 2. Calculate Buying Cost (P&I, maintenance, tax, transaction fees minus equity gained)
      const loanAmount = Math.max(0, homePrice - downPayment);
      const r = interestRate / 12;
      const n = termYears * 12;
      let monthlyPI = 0;
      if (r === 0) {
        monthlyPI = loanAmount / n;
      } else {
        monthlyPI = (loanAmount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
      }

      // Over 10 years
      const totalPIPaid = monthlyPI * months;
      
      // Calculate remaining mortgage balance after 10 years
      let balance = loanAmount;
      let totalInterestPaid = 0;
      let totalPrincipalPaid = 0;
      for (let m = 0; m < months; m++) {
        const interestM = balance * r;
        const principalM = Math.min(balance, monthlyPI - interestM);
        totalInterestPaid += interestM;
        totalPrincipalPaid += principalM;
        balance -= principalM;
      }

      // Maintenance costs (compounding value or flat %)
      const initialMaintenance = homePrice * maintenancePercent;
      let totalMaintenancePaid = 0;
      let currentMaint = initialMaintenance;
      for (let y = 0; y < yearsToCompare; y++) {
        totalMaintenancePaid += currentMaint;
        currentMaint *= (1 + 0.025); // 2.5% inflation on services
      }

      // Closing costs buying (approx 2% of price) + selling (approx 6% fee of future price)
      const futureHomeValue = homePrice * Math.pow(1 + appreciationRate, yearsToCompare);
      const buyingClosingCosts = homePrice * 0.02;
      const sellingClosingCosts = futureHomeValue * 0.06;

      // Cumulative purchase costs
      const cumulativeSpentBuy = downPayment + totalPIPaid + totalMaintenancePaid + buyingClosingCosts + sellingClosingCosts;
      
      // Home Equity Gained
      const netSellingProceeds = futureHomeValue - balance - sellingClosingCosts;
      const netBuyingCost = cumulativeSpentBuy - netSellingProceeds;

      const netBenefit = totalRentPaid - netBuyingCost;
      const recommend = netBenefit > 0
        ? `Over a 10-year period, Buying is projected to save you $${Math.abs(netBenefit).toLocaleString("en-US", { maximumFractionDigits: 0 })} compared to renting. This is mainly due to $${(futureHomeValue - homePrice).toLocaleString("en-US", { maximumFractionDigits: 0 })} of projected home appreciation.`
        : `Over a 10-year period, Renting is projected to be more cost-effective by $${Math.abs(netBenefit).toLocaleString("en-US", { maximumFractionDigits: 0 })}. Buying costs such as transaction fees and maintenance exceed the estimated wealth built through home appreciation and principal paydown.`;

      return {
        outputs: [
          { label: "Total Rent Cost (10 Yrs)", value: totalRentPaid, type: "currency" },
          { label: "Net Buy Cost (10 Yrs)", value: netBuyingCost, type: "currency" },
          { label: "Equity Built in 10 Yrs", value: futureHomeValue - balance, type: "currency" },
          { label: "Estimated Buying Benefit", value: netBenefit, type: "currency", highlighted: true }
        ],
        chartData: [
          { name: "Rent Payments", value: totalRentPaid },
          { name: "Buying Net Spent", value: netBuyingCost },
          { name: "Future Home Value", value: futureHomeValue },
          { name: "Home Equity", value: futureHomeValue - balance }
        ],
        recommendation: recommend
      };
    },
    guide: {
      overview: "Determine whether renting or buying a home makes more financial sense based on expenses, home appreciation, and timeline.",
      howToUse: "Enter your monthly rent estimate, purchase price, down payment, mortgage rate, and expected appreciation of the asset.",
      formulaExplanation: "Calculates total rent paid (factoring in annual rent inflation) and compares it to cumulative home buying cash flow (monthly payment, tax, maintenance, transaction fees, interest) minus equity growth.",
      faq: [
        { question: "How long should I stay in a home to favor buying?", answer: "Usually, the 'break-even' point falls between 4 to 7 years. Shorter timelines favor renting due to high loan transaction fees." },
        { question: "Does this factor in taxes?", answer: "Yes, it accounts for home ownership costs like standard mortgage interest, closing taxes, and recurring property taxes." }
      ],
      seoContent800Words: "Placeholder SEO Content"
    }
  },
  {
    id: "house-affordability",
    name: "House Affordability Calculator",
    description: "Calculate how much home you can comfortably afford based on your household earnings and ongoing monthly debts.",
    category: "loans",
    inputs: [
      { id: "annualIncome", label: "Annual Household Gross Income ($)", type: "currency", defaultValue: 110000, min: 20000, max: 1000000, step: 2500 },
      { id: "monthlyDebt", label: "Monthly Debt Payments ($)", type: "currency", defaultValue: 550, min: 0, max: 10000, step: 50 },
      { id: "downPayment", label: "Down Payment ($)", type: "currency", defaultValue: 50000, min: 0, max: 1000000, step: 2000 },
      { id: "interestRate", label: "Annual Mortgage Rate (%)", type: "percentage", defaultValue: 6.8, min: 1, max: 15, step: 0.1 },
      { id: "taxInsurance", label: "Annual Property Taxes & Insurance (%)", type: "percentage", defaultValue: 2.2, min: 0.5, max: 6, step: 0.1 },
      { id: "debtToIncome", label: "Target Debt-to-Income Limit (%)", type: "percentage", defaultValue: 36, min: 20, max: 50, step: 1 }
    ],
    calculate: (inputs) => {
      const annualIncome = inputs.annualIncome || 110000;
      const monthlyDebt = inputs.monthlyDebt || 550;
      const downPayment = inputs.downPayment || 50000;
      const interestRate = (inputs.interestRate || 6.8) / 100;
      const taxInsurance = (inputs.taxInsurance || 2.2) / 100;
      const debtToIncome = (inputs.debtToIncome || 36) / 100;

      const monthlyGross = annualIncome / 12;
      const maxMonthlyTotalDebt = monthlyGross * debtToIncome;
      const maxMonthlyHousing = Math.max(0, maxMonthlyTotalDebt - monthlyDebt);

      // Solve for Home Price P:
      // Monthly Housing = P&I + Taxes/Insurance
      // P&I = (P - D) * [ r(1+r)^N / ((1+r)^N - 1) ] = (P - D) * k
      // Monthly Taxes/Insurance = P * (taxInsurance / 12) = P * h
      // MaxMonthlyHousing = Pk - Dk + Ph => P(k + h) = MaxMonthlyHousing + Dk => P = (MaxMonthlyHousing + Dk) / (k + h)
      const r = interestRate / 12;
      const n = 30 * 12; // Standard 30 year
      const k = (r === 0) ? (1 / n) : (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
      const h = taxInsurance / 12;

      let affordableHomePrice = 0;
      if (k + h > 0) {
        affordableHomePrice = (maxMonthlyHousing + downPayment * k) / (k + h);
      }
      
      const loanAmount = Math.max(0, affordableHomePrice - downPayment);
      const monthlyPI = loanAmount * k;
      const monthlyTaxIns = affordableHomePrice * h;

      const recommend = `Based on a ${debtToIncome * 100}% DTI limit, you can comfortably afford a home priced up to $${Math.round(affordableHomePrice).toLocaleString("en-US")}. This keeps your monthly mortgage payment (P&I plus tax & insurance) at $${Math.round(maxMonthlyHousing).toLocaleString("en-US")}.`;

      return {
        outputs: [
          { label: "Max Affordable Home Price", value: affordableHomePrice, type: "currency", highlighted: true },
          { label: "Recommended Max Monthly Payment", value: maxMonthlyHousing, type: "currency" },
          { label: "Projected Mortgage Loan amount", value: loanAmount, type: "currency" },
          { label: "Monthly Principal & Interest", value: monthlyPI, type: "currency" }
        ],
        chartData: [
          { name: "Monthly Debt Obligations", value: monthlyDebt },
          { name: "Home P&I Payment", value: monthlyPI },
          { name: "Taxes & Home Insurance", value: monthlyTaxIns },
          { name: "Gross Monthly Income", value: monthlyGross }
        ],
        recommendation: recommend
      };
    },
    guide: {
      overview: "Determine your maximum home purchasing budget based on DTI ratio standards used by bank underwriters.",
      howToUse: "Provide your annual income, outstanding monthly payments (cars, credit cards, student loans), your down payment size, and the mortgage rate.",
      formulaExplanation: "Solves a systemic equation factoring in loan duration, monthly compound interest, and fractional annual tax/insurance percentages against your custom monthly debt-to-income limits.",
      faq: [
        { question: "What is the 36% rule of thumb?", answer: "Many lenders prefer your total monthly debts (including your new mortgage) to not exceed 36% of your pre-tax income." },
        { question: "Does a solar down payment increase my budget?", answer: "Yes, every dollar added to your down payment directly increases your budget by one dollar plus reduces your lifetime interest charges." }
      ],
      seoContent800Words: "Placeholder SEO Content"
    }
  },
  {
    id: "mortgage-prepayment",
    name: "Mortgage Prepayment Calculator",
    description: "Determine how much time and interest you will save by making extra monthly or yearly principal payments on your mortgage.",
    category: "loans",
    inputs: [
      { id: "loanAmount", label: "Current Balance / Loan Amount ($)", type: "currency", defaultValue: 320000, min: 20000, max: 2000000, step: 5000 },
      { id: "interestRate", label: "Annual Interest Rate (%)", type: "percentage", defaultValue: 6.8, min: 1, max: 15, step: 0.1 },
      { id: "termYears", label: "Remaining Term (Years)", type: "number", defaultValue: 30, min: 5, max: 30, step: 1 },
      { id: "extraPayment", label: "Extra Monthly Payment ($)", type: "currency", defaultValue: 250, min: 0, max: 10000, step: 25 }
    ],
    calculate: (inputs) => {
      const loanAmount = inputs.loanAmount || 320000;
      const interestRate = (inputs.interestRate || 6.8) / 100;
      const termYears = inputs.termYears || 30;
      const extraPayment = inputs.extraPayment || 250;

      const r = interestRate / 12;
      const totalMonths = termYears * 12;
      
      const standardPayment = (r === 0) 
        ? loanAmount / totalMonths 
        : (loanAmount * r * Math.pow(1 + r, totalMonths)) / (Math.pow(1 + r, totalMonths) - 1);

      // Simple Simulation of Regular Payoff
      let balanceReg = loanAmount;
      let interestReg = 0;
      for (let m = 0; m < totalMonths; m++) {
        const intM = balanceReg * r;
        const prinM = Math.min(balanceReg, standardPayment - intM);
        interestReg += intM;
        balanceReg -= prinM;
      }

      // Prepayment Simulation
      let balancePrep = loanAmount;
      let interestPrep = 0;
      let prepMonths = 0;
      while (balancePrep > 0 && prepMonths < 600) {
        const intM = balancePrep * r;
        const totalPaidM = Math.min(balancePrep + intM, standardPayment + extraPayment);
        const prinM = totalPaidM - intM;
        interestPrep += intM;
        balancePrep -= prinM;
        prepMonths++;
      }

      const interestSaved = Math.max(0, interestReg - interestPrep);
      const monthsSaved = Math.max(0, totalMonths - prepMonths);
      const yearsSaved = monthsSaved / 12;

      const recommend = interestSaved > 0 
        ? `By paying an extra $${extraPayment} each month, you will save $${Math.round(interestSaved).toLocaleString("en-US")} in lifetime interest and pay off your mortgage ${yearsSaved.toFixed(1)} years early.`
        : `Enter an extra monthly payment to calculate your potential time and interest savings.`;

      return {
        outputs: [
          { label: "Standard Interest Cost", value: interestReg, type: "currency" },
          { label: "Interest Cost with Extra Pay", value: interestPrep, type: "currency" },
          { label: "Total Interest Saved", value: interestSaved, type: "currency", highlighted: true },
          { label: "Years Saved Off Mortgage", value: yearsSaved, type: "number", highlighted: true }
        ],
        chartData: [
          { name: "Standard Interest Paid", value: interestReg },
          { name: "Extra Plan Interest Paid", value: interestPrep },
          { name: "Principal Amount", value: loanAmount },
          { name: "Total Interest Saved", value: interestSaved }
        ],
        recommendation: recommend
      };
    },
    guide: {
      overview: "See the long-term impact of adding regular extra payments to your primary mortgage balance.",
      howToUse: "Enter your primary mortgage amount, annual interest rate, years left on the loan, and your intended extra monthly dollars.",
      formulaExplanation: "Schedules month-by-month principal decrements, verifying when the loan closes and subtracting standard interest projections.",
      faq: [
        { question: "Is there a penalty for prepaying a mortgage?", answer: "Most modern residential home loans have no prepayment penalties, but check with your specific servicer first." },
        { question: "Does prepaying affect my tax write-off?", answer: "Yes, because you pay less mortgage interest over time, your itemized tax deductions will decrease, but the direct interest savings far outweigh any tax benefits." }
      ],
      seoContent800Words: "Placeholder SEO Content"
    }
  },
  {
    id: "biweekly-mortgage",
    name: "Biweekly Mortgage Calculator",
    description: "Compare a standard 12-month schedule containing monthly payments against building equity using a 26-payment biweekly system.",
    category: "loans",
    inputs: [
      { id: "homePrice", label: "Home Base Val ($)", type: "currency", defaultValue: 380000, min: 50000, max: 2000000, step: 5000 },
      { id: "downPayment", label: "Down Payment ($)", type: "currency", defaultValue: 76000, min: 0, max: 1000000, step: 2000 },
      { id: "interestRate", label: "Annual Interest Rate (%)", type: "percentage", defaultValue: 6.8, min: 1, max: 15, step: 0.1 },
      { id: "termYears", label: "Mortgage Term (Years)", type: "number", defaultValue: 30, min: 10, max: 30, step: 5 }
    ],
    calculate: (inputs) => {
      const homePrice = inputs.homePrice || 380000;
      const downPayment = inputs.downPayment || 76000;
      const interestRate = (inputs.interestRate || 6.8) / 100;
      const termYears = inputs.termYears || 30;

      const loanAmount = Math.max(0, homePrice - downPayment);
      const totalMonths = termYears * 12;
      const monthlyRate = interestRate / 12;

      // Standard Monthly Payment
      const standardPayment = (monthlyRate === 0)
        ? loanAmount / totalMonths
        : (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) / (Math.pow(1 + monthlyRate, totalMonths) - 1);

      let balanceReg = loanAmount;
      let totalInterestReg = 0;
      for (let m = 0; m < totalMonths; m++) {
        const intM = balanceReg * monthlyRate;
        const prinM = Math.min(balanceReg, standardPayment - intM);
        totalInterestReg += intM;
        balanceReg -= prinM;
      }

      // Biweekly standard: you pay exactly half the standard payment every 2 weeks (26 times a year)
      // This effectively means paying 13 full monthly payments every 12 calendar months.
      const biweeklyPayment = standardPayment / 2;
      const biweeklyRate = interestRate / 26; // 26 biweekly periods in a year
      
      let balanceBi = loanAmount;
      let totalInterestBi = 0;
      let biPeriods = 0;
      while (balanceBi > 0 && biPeriods < 1500) {
        const intP = balanceBi * biweeklyRate;
        const paymentApplied = Math.min(balanceBi + intP, biweeklyPayment);
        totalInterestBi += intP;
        balanceBi -= (paymentApplied - intP);
        biPeriods++;
      }

      const yearsBi = biPeriods / 26;
      const yearsSaved = Math.max(0, termYears - yearsBi);
      const interestSaved = Math.max(0, totalInterestReg - totalInterestBi);

      const recommend = `Transitioning to a biweekly payment model pays off your home loan in ${yearsBi.toFixed(1)} years instead of ${termYears}, saving you $${Math.round(interestSaved).toLocaleString("en-US")} in lifetime interest.`;

      return {
        outputs: [
          { label: "Monthly Payment (Half & Half)", value: standardPayment, type: "currency" },
          { label: "Biweekly Payment Amount", value: biweeklyPayment, type: "currency" },
          { label: "Payoff Term with Biweekly (Yrs)", value: yearsBi, type: "number", highlighted: true },
          { label: "Mortgage Interest Saved", value: interestSaved, type: "currency", highlighted: true }
        ],
        chartData: [
          { name: "Monthly Total Interest", value: totalInterestReg },
          { name: "Biweekly Total Interest", value: totalInterestBi },
          { name: "Biweekly Interest Savings", value: interestSaved }
        ],
        recommendation: recommend
      };
    },
    guide: {
      overview: "Compare standard monthly home loans against accelerated biweekly payment processing models.",
      howToUse: "Enter your purchase amount, down payment, expected interest rate, and nominal mortgage length to compare payoffs.",
      formulaExplanation: "Calculates interest at a biweekly step rate of R/26, running payments of half the monthly calculation 26 times per year.",
      faq: [
        { question: "Why does a biweekly mortgage save so much?", answer: "Because there are 52 weeks in a year, paying every 2 weeks results in 26 half-payments, which translates to 13 full monthly payments per year instead of 12. This extra payment goes entirely toward principal." },
        { question: "Can I set this up with my current lender?", answer: "Most mortgage servicers offer biweekly payment plans, though some may charge minor setup fees. Alternatively, you can achieve the same benefit by adding 1/12th of your monthly payment to each standard payment." }
      ],
      seoContent800Words: "Placeholder SEO Content"
    }
  },
  {
    id: "mortgage-points",
    name: "Mortgage Points Calculator",
    description: "Calculate if buying discount rate points upfront makes sense based on your calculated breakeven horizon.",
    category: "loans",
    inputs: [
      { id: "loanAmount", label: "Projected Mortgage Loan ($)", type: "currency", defaultValue: 300000, min: 20000, max: 2000000, step: 5000 },
      { id: "originalRate", label: "Standard Mortgage Rate (%)", type: "percentage", defaultValue: 7.2, min: 1, max: 15, step: 0.1 },
      { id: "pointsBought", label: "Discount Points Purchased", type: "number", defaultValue: 1.5, min: 0.25, max: 5, step: 0.25 },
      { id: "termYears", label: "Mortgage Term (Years)", type: "number", defaultValue: 30, min: 10, max: 30, step: 5 }
    ],
    calculate: (inputs) => {
      const loanAmount = inputs.loanAmount || 300000;
      const originalRate = (inputs.originalRate || 7.2) / 100;
      const pointsBought = inputs.pointsBought || 1.5;
      const termYears = inputs.termYears || 30;

      // Cost of purchase points: 1 point = 1% of loan amount
      const upfrontPointsCost = loanAmount * (pointsBought / 100);

      // Rate discount: 1 point typically reduces interest rate by 0.25%
      const interestRateReduction = pointsBought * 0.25;
      const discountedRate = Math.max(0.01, originalRate - (interestRateReduction / 100));

      const n = termYears * 12;
      const rOrig = originalRate / 12;
      const rDisc = discountedRate / 12;

      const monthlyBasePI = (loanAmount * rOrig * Math.pow(1 + rOrig, n)) / (Math.pow(1 + rOrig, n) - 1);
      const monthlyDiscountedPI = (loanAmount * rDisc * Math.pow(1 + rDisc, n)) / (Math.pow(1 + rDisc, n) - 1);

      const monthlySavings = Math.max(0, monthlyBasePI - monthlyDiscountedPI);
      const breakevenMonths = monthlySavings > 0 ? upfrontPointsCost / monthlySavings : 0;
      const breakevenYears = breakevenMonths / 12;

      const totalLifetimeSavings = (monthlySavings * n) - upfrontPointsCost;

      const recommend = breakevenMonths > 0
        ? `Buying ${pointsBought} points costs $${Math.round(upfrontPointsCost).toLocaleString("en-US")} upfront and saves $${monthlySavings.toFixed(2)} monthly. You break even in ${breakevenMonths.toFixed(1)} months (${breakevenYears.toFixed(1)} years). Keep this mortgage longer than that to make it profitable!`
        : `Adjust your discount points inputs.`;

      return {
        outputs: [
          { label: "Upfront Cost of Points", value: upfrontPointsCost, type: "currency" },
          { label: "Original Monthly P&I", value: monthlyBasePI, type: "currency" },
          { label: "New Discounted Monthly P&I", value: monthlyDiscountedPI, type: "currency" },
          { label: "Monthly Payment Savings", value: monthlySavings, type: "currency", highlighted: true },
          { label: "Breakeven Horizon (Years)", value: breakevenYears, type: "number", highlighted: true }
        ],
        chartData: [
          { name: "Upfront Point Cost", value: upfrontPointsCost },
          { name: "10-Year Payment Savings", value: monthlySavings * 120 },
          { name: "Estimated Total Savings", value: totalLifetimeSavings }
        ],
        recommendation: recommend
      };
    },
    guide: {
      overview: "Determine if paying upfront discount fees to lower your mortgage interest rate provides a strong financial return.",
      howToUse: "Enter your loan amount, base mortgage interest rate, number of mortgage points to buy, and loan term.",
      formulaExplanation: "Computes the exact dollar savings in mortgage payments and divides the upfront cash outlay by that monthly savings value.",
      faq: [
        { question: "What is a mortgage discount point?", answer: "A point is pre-paid interest that reduces your ongoing home mortgage interest rate by roughly 0.25% per full point purchased." },
        { question: "When are points generally a bad idea?", answer: "If you plan to sell the property or refinance the mortgage within 3 to 5 years, you likely won't reach the break-even point to recoup the upfront costs." }
      ],
      seoContent800Words: "Placeholder SEO Content"
    }
  },
  {
    id: "closing-costs-calculator",
    name: "Closing Cost Calculator",
    description: "Estimate the comprehensive closing costs required to finalize your mortgage and home purchase.",
    category: "loans",
    inputs: [
      { id: "homePrice", label: "Property Purchase Price ($)", type: "currency", defaultValue: 350000, min: 30000, max: 2500000, step: 5000 },
      { id: "loanAmount", label: "Projected Mortgage Loan ($)", type: "currency", defaultValue: 280000, min: 10000, max: 2000000, step: 5000 },
      { id: "stateTaxRate", label: "Transfer Taxes (% of home value)", type: "percentage", defaultValue: 1.1, min: 0, max: 5, step: 0.05 },
      { id: "brokerFeeRate", label: "Attorney & Escrow Fees (% of loan value)", type: "percentage", defaultValue: 0.85, min: 0, max: 4, step: 0.05 },
      { id: "escrowMonths", label: "Escrow Reserve Prepaids (Months)", type: "number", defaultValue: 3, min: 0, max: 12, step: 1 }
    ],
    calculate: (inputs) => {
      const homePrice = inputs.homePrice || 350000;
      const loanAmount = inputs.loanAmount || 280000;
      const stateTaxRate = (inputs.stateTaxRate || 1.1) / 100;
      const brokerFeeRate = (inputs.brokerFeeRate || 0.85) / 100;
      const escrowMonths = inputs.escrowMonths || 3;

      const lenderOrigination = loanAmount * 0.01; // 1% default origination fee
      const appraisalCredit = 650; // Standard flat third party fee
      const titleServices = homePrice * 0.0075; // Title insurance and registration
      const governmentTransferText = homePrice * stateTaxRate;
      const legalEscrowSvc = loanAmount * brokerFeeRate;
      const recordingFee = 150;

      // Escrow prepaids (approx taxes and home insurance)
      const annualTaxEst = homePrice * 0.012; // 1.2% tax baseline
      const annualInsEst = 1400; // Flat estimate
      const monthlyEscrow = (annualTaxEst + annualInsEst) / 12;
      const escrowPrepaids = monthlyEscrow * escrowMonths;

      const totalClosingCosts = lenderOrigination + appraisalCredit + titleServices + governmentTransferText + legalEscrowSvc + recordingFee + escrowPrepaids;
      const costPercent = (totalClosingCosts / homePrice) * 100;

      const recommend = `Estimated closing costs for your purchase are $${Math.round(totalClosingCosts).toLocaleString("en-US")} (approx ${costPercent.toFixed(2)}% of the purchase price). Be prepared to pay this cash upfront in addition to your down payment.`;

      return {
        outputs: [
          { label: "Total Estimated Closing Costs", value: totalClosingCosts, type: "currency", highlighted: true },
          { label: "Percentage of Home price", value: costPercent, type: "percentage" },
          { label: "Lender Origination fees", value: lenderOrigination, type: "currency" },
          { label: "Escrow Prepaids Reserve", value: escrowPrepaids, type: "currency" }
        ],
        chartData: [
          { name: "Lender Origination", value: lenderOrigination },
          { name: "Government Taxes", value: governmentTransferText },
          { name: "Attorney & Escrow Fees", value: legalEscrowSvc },
          { name: "Prepay Escrows", value: escrowPrepaids },
          { name: "Title Services", value: titleServices }
        ],
        recommendation: recommend
      };
    },
    guide: {
      overview: "Break down and estimate the processing, tax, and registration fees needed to close your real estate transaction.",
      howToUse: "Enter your home purchase price, expected mortgage amount, standard state transfer tax rates, and target escrow months.",
      formulaExplanation: "Sums loan origination fees, appraisal flat charges, transfer stamp values, legal recording, and advance escrow reserve cushions.",
      faq: [
        { question: "Can closing costs be rolled into the loan?", answer: "For purchases, closing costs must usually be paid in cash at settlement, although sellers may offer concession credits. For refinancing, these costs can often be rolled into the loan balance." },
        { question: "What are escrow prepaids?", answer: "These are upfront reserves collected by lenders to pre-fund your property taxes and home insurance, ensuring those bills are paid on time." }
      ],
      seoContent800Words: "Placeholder SEO Content"
    }
  },
  {
    id: "pmi-calculator",
    name: "PMI Calculator",
    description: "Determine the monthly cost of Private Mortgage Insurance (PMI) and when you can cancel it.",
    category: "loans",
    inputs: [
      { id: "homePrice", label: "Property Value ($)", type: "currency", defaultValue: 380000, min: 50000, max: 2000000, step: 5000 },
      { id: "downPayment", label: "Down Payment ($)", type: "currency", defaultValue: 38000, min: 0, max: 500000, step: 2000 },
      { id: "creditScore", label: "FICO Credit Score", type: "number", defaultValue: 720, min: 620, max: 850, step: 10 },
      { id: "interestRate", label: "Mortgage Interest Rate (%)", type: "percentage", defaultValue: 6.8, min: 1, max: 15, step: 0.1 }
    ],
    calculate: (inputs) => {
      const homePrice = inputs.homePrice || 380000;
      const downPayment = inputs.downPayment || 38000;
      const creditScore = inputs.creditScore || 720;
      const interestRate = (inputs.interestRate || 6.8) / 100;

      const ltvValue = ((homePrice - downPayment) / homePrice) * 100;
      const loanAmount = Math.max(0, homePrice - downPayment);

      let pmiBaseRate = 0;
      if (ltvValue > 80) {
        // Evaluate FICO ranges for private mortgage premiums
        if (creditScore >= 760) pmiBaseRate = 0.45 / 100;
        else if (creditScore >= 720) pmiBaseRate = 0.65 / 100;
        else if (creditScore >= 680) pmiBaseRate = 0.95 / 100;
        else pmiBaseRate = 1.35 / 100;

        // Scale by intensity of leverage ratio
        if (ltvValue > 95) pmiBaseRate *= 1.15;
        else if (ltvValue > 90) pmiBaseRate *= 1.05;
      }

      const annualPmi = loanAmount * pmiBaseRate;
      const monthlyPmi = annualPmi / 12;

      // Approximate years to cancel (estimate with 3.5% home appreciation + scheduled principal paydown)
      const r = interestRate / 12;
      const n = 30 * 12;
      const monthlyPI = (loanAmount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
      
      let balance = loanAmount;
      let currHomeVal = homePrice;
      let monthsTo80Ltv = 0;
      
      while (balance / currHomeVal > 0.8 && monthsTo80Ltv < 360) {
        const intM = balance * r;
        const prinM = Math.min(balance, monthlyPI - intM);
        balance -= prinM;
        currHomeVal *= Math.pow(1 + 0.035, 1/12); // Standard 3.5% appreciation
        monthsTo80Ltv++;
      }
      
      const yearsTo80Ltv = monthsTo80Ltv / 12;

      const recommend = ltvValue <= 80
        ? "Congratulations! You have put down 20% or more, meaning you avoid PMI payments entirely."
        : `Your estimated monthly PMI is $${monthlyPmi.toFixed(2)}. It should take roughly ${yearsTo80Ltv.toFixed(1)} years of scheduled payments and moderate 3.5% property growth to drop your loan-to-value (LTV) below 80% to cancel the insurance.`;

      return {
        outputs: [
          { label: "Estimated Monthly PMI Cost", value: monthlyPmi, type: "currency", highlighted: ltvValue > 80 },
          { label: "Private Insurance Rate Premium", value: pmiBaseRate * 100, type: "percentage" },
          { label: "Current Leverage LTV Ratio", value: ltvValue, type: "percentage" },
          { label: "Estimated Years to Cancel PMI", value: ltvValue > 80 ? yearsTo80Ltv : 0, type: "number", highlighted: ltvValue > 80 }
        ],
        chartData: [
          { name: "Monthly PMI premium", value: monthlyPmi },
          { name: "Monthly Principal & Interest", value: monthlyPI },
          { name: "Remaining Principal Owed", value: loanAmount / 1000 }
        ],
        recommendation: recommend
      };
    },
    guide: {
      overview: "Calculate your premium for Private Mortgage Insurance (PMI) if purchasing with less than a 20% down payment.",
      howToUse: "Enter your target home price, down payment, credit score range, and standard annual interest rate.",
      formulaExplanation: "Uses mortgage FICO pricing criteria tables and scales payments by loan leverage metrics (LTV%), forecasting home equity expansion to find the cancellation window.",
      faq: [
        { question: "How can I avoid paying PMI?", answer: "You can avoid PMI by putting down 20% or more on your home purchase, or by using specialized loan programs like VA loans or interest-hiking piggyback financing structures." },
        { question: "Does PMI protect me if I miss payments?", answer: "No, PMI protects the lender, not you, in the event that you default on the loan." }
      ],
      seoContent800Words: "Placeholder SEO Content"
    }
  },
  {
    id: "cash-out-refinance",
    name: "Cash-Out Refinance Calculator",
    description: "Determine how much equity you can extract from your home and preview your projected refinance payments.",
    category: "loans",
    inputs: [
      { id: "homeValue", label: "Estimated Market Value ($)", type: "currency", defaultValue: 450000, min: 50000, max: 2000000, step: 10000 },
      { id: "morBalance", label: "Outstanding Mortgage Owed ($)", type: "currency", defaultValue: 215000, min: 0, max: 1500000, step: 5000 },
      { id: "cashNeeded", label: "Desired Cash-Out Principal ($)", type: "currency", defaultValue: 65000, min: 2000, max: 500000, step: 1000 },
      { id: "newRate", label: "New Annual Interest Rate (%)", type: "percentage", defaultValue: 6.5, min: 1, max: 15, step: 0.1 },
      { id: "newTerm", label: "New Mortgage Term (Yrs)", type: "number", defaultValue: 30, min: 10, max: 30, step: 5 }
    ],
    calculate: (inputs) => {
      const homeValue = inputs.homeValue || 450000;
      const currentMortgage = inputs.morBalance || 215000;
      const cashNeeded = inputs.cashNeeded || 65000;
      const newRate = (inputs.newRate || 6.5) / 100;
      const newTerm = inputs.newTerm || 30;

      // Banks usually limit cash-out refinances to 80% Loan-to-Value (LTV)
      const maxLtv = 0.80;
      const maxTotalLoanAllowed = homeValue * maxLtv;
      const maxCashExtractable = Math.max(0, maxTotalLoanAllowed - currentMortgage);

      const requestedLoanTotal = currentMortgage + cashNeeded;
      const actualCashOut = Math.min(cashNeeded, maxCashExtractable);
      const finalNewLoanAmount = currentMortgage + actualCashOut;

      const r = newRate / 12;
      const n = newTerm * 12;
      const newMonthlyPayment = (r === 0)
        ? finalNewLoanAmount / n
        : (finalNewLoanAmount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);

      const endingLtv = (finalNewLoanAmount / homeValue) * 100;

      let recommend = "";
      if (requestedLoanTotal > maxTotalLoanAllowed) {
        recommend = `Warning: Your desired cash-out exceeds the standard 80% LTV limit of $${Math.round(maxTotalLoanAllowed).toLocaleString("en-US")}. Your cash extraction is capped at $${Math.round(maxCashExtractable).toLocaleString("en-US")} to stay compliant.`;
      } else {
        recommend = `You qualify to extract the full cash amount of $${Math.round(actualCashOut).toLocaleString("en-US")}. This brings your new mortgage balance to $${Math.round(finalNewLoanAmount).toLocaleString("en-US")} at a ${endingLtv.toFixed(1)}% LTV ratio.`;
      }

      return {
        outputs: [
          { label: "Max Available Cash (80% LTV)", value: maxCashExtractable, type: "currency" },
          { label: "Projected Cash-Out Disbursed", value: actualCashOut, type: "currency", highlighted: true },
          { label: "Total Refinanced Loan Balance", value: finalNewLoanAmount, type: "currency" },
          { label: "New Monthly P&I Payment", value: newMonthlyPayment, type: "currency", highlighted: true }
        ],
        chartData: [
          { name: "Current Loan Payoff", value: currentMortgage },
          { name: "Net Cash to You", value: actualCashOut },
          { name: "Preserved Equity Buffer", value: homeValue - finalNewLoanAmount }
        ],
        recommendation: recommend
      };
    },
    guide: {
      overview: "Determine the maximum amount of cash you can pull from home asset equity.",
      howToUse: "Specify your home values, outstanding mortgage, cash required, and refinanced rate expectations.",
      formulaExplanation: "Calculates loan limits at 80% LTV, deducts current balance, and simulates a brand-new amortization schedule.",
      faq: [
        { question: "Can I use refinance cash for any purpose?", answer: "Yes, once disbursed, you can use refinance proceeds for home improvements, consolidation of consumer debts, or personal investments." },
        { question: "What closing costs should I expect?", answer: "Closing costs typically average 2% to 5% of the total refinanced loan amount." }
      ],
      seoContent800Words: "Placeholder SEO Content"
    }
  },
  {
    id: "reverse-mortgage",
    name: "Reverse Mortgage Calculator",
    description: "Calculate standard cash payouts and principal limits available via Home Equity Conversion Mortgages (HECM).",
    category: "loans",
    inputs: [
      { id: "homeValue", label: "Estimated Home Value ($)", type: "currency", defaultValue: 450000, min: 100000, max: 2500000, step: 10000 },
      { id: "currentMortgage", label: "Existing Mortgage Balance ($)", type: "currency", defaultValue: 45000, min: 0, max: 1000000, step: 2000 },
      { id: "borrowerAge", label: "Youngest Owner Age", type: "number", defaultValue: 70, min: 62, max: 95, step: 1 },
      { id: "interestRate", label: "Expected Closing Rate (%)", type: "percentage", defaultValue: 7.0, min: 3, max: 12, step: 0.1 }
    ],
    calculate: (inputs) => {
      const homeValue = inputs.homeValue || 450000;
      const currentMortgage = inputs.currentMortgage || 45000;
      const borrowerAge = inputs.borrowerAge || 70;
      const interestRate = inputs.interestRate || 7.0;

      // Reverse mortgages require borrower to be at least age 62
      if (borrowerAge < 62) {
        return {
          outputs: [
            { label: "Eligible Age Status", value: "Not Eligible (Must be 62+)", type: "text", highlighted: true }
          ],
          recommendation: "Under HECM rules, the youngest homeowner must be at least 62 years of age to access reverse mortgage products."
        };
      }

      // Principal Limit Factors (PLF) lookup model
      // PLF increases with age and decreases with interest rates
      let basePlf = 0.40;
      if (borrowerAge >= 85) basePlf = 0.65;
      else if (borrowerAge >= 80) basePlf = 0.58;
      else if (borrowerAge >= 75) basePlf = 0.52;
      else if (borrowerAge >= 70) basePlf = 0.47;
      else if (borrowerAge >= 65) basePlf = 0.43;

      // Rate penalty: subtract 1.5% PLF for every 1% interest rates exceed 5%
      const rateExcess = Math.max(0, interestRate - 5);
      const adjustedPlf = Math.max(0.1, basePlf - (rateExcess * 0.015));

      const grossPrincipalLimit = homeValue * adjustedPlf;
      const netPrincipalLimit = Math.max(0, grossPrincipalLimit - currentMortgage);

      // Estimated Monthly Tenure (receivable over remainder of life, assuming cap of 100)
      const planningYears = Math.max(10, 100 - borrowerAge);
      const tenureMonthlyPayment = netPrincipalLimit / (planningYears * 12);

      const recommend = netPrincipalLimit > 0
        ? `You qualify for a dynamic gross limit of $${Math.round(grossPrincipalLimit).toLocaleString("en-US")}. After paying off your existing mortgage of $${currentMortgage.toLocaleString("en-US")}, you will receive a net amount of $${Math.round(netPrincipalLimit).toLocaleString("en-US")} in proceeds.`
        : `Your outstanding debt of $${currentMortgage.toLocaleString("en-US")} exceeds the estimated reverse mortgage principal limit of $${Math.round(grossPrincipalLimit).toLocaleString("en-US")}. You must pay down this balance to qualify.`;

      return {
        outputs: [
          { label: "Gross Principal Limit", value: grossPrincipalLimit, type: "currency" },
          { label: "Required Mortgage Payoff", value: currentMortgage, type: "currency" },
          { label: "Net Cash Proceeds Available", value: netPrincipalLimit, type: "currency", highlighted: true },
          { label: "Estimated Monthly Tenure payout", value: netPrincipalLimit > 0 ? tenureMonthlyPayment : 0, type: "currency", highlighted: true }
        ],
        chartData: [
          { name: "Mandatory Loan Payoff", value: currentMortgage },
          { name: "Net Liquid Cash", value: netPrincipalLimit },
          { name: "Remaining House Equity", value: homeValue - grossPrincipalLimit }
        ],
        recommendation: recommend
      };
    },
    guide: {
      overview: "Determine available funds from HUD-backed Home Equity Conversion Mortgages.",
      howToUse: "Enter estimated home value, current loan balances, and youngest homeowner age.",
      formulaExplanation: "Calculates funding limits by applying age-indexed PLFs (Principal Limit Factors) and subtracting current home lien payoff requirements.",
      faq: [
        { question: "What is a reverse mortgage?", answer: "It is a specialized financial product that allows seniors to convert home equity into tax-free cash payments without making monthly mortgage bills." },
        { question: "When does the reverse loan need to be paid back?", answer: "Typically, the balance is due only when the last surviving borrower moves out of the home permanently, sells the property, or passes away." }
      ],
      seoContent800Words: "Placeholder SEO Content"
    }
  },
  {
    id: "home-appreciation",
    name: "Home Appreciation Calculator",
    description: "Determine the future projected value of your home using historical real estate inflation trends.",
    category: "loans",
    inputs: [
      { id: "initialValue", label: "Current Purchase / Value ($)", type: "currency", defaultValue: 350000, min: 20000, max: 2000000, step: 5000 },
      { id: "appreciationRate", label: "Annual Rate of Appreciation (%)", type: "percentage", defaultValue: 3.8, min: -5, max: 15, step: 0.1 },
      { id: "years", label: "Duration Horizon (Years)", type: "number", defaultValue: 10, min: 1, max: 40, step: 1 },
      { id: "renovationSpend", label: "Optional Home Renovations ($)", type: "currency", defaultValue: 15000, min: 0, max: 250000, step: 1000 }
    ],
    calculate: (inputs) => {
      const initialValue = inputs.initialValue || 350000;
      const appreciationRate = (inputs.appreciationRate || 3.8) / 100;
      const years = inputs.years || 10;
      const renovationSpend = inputs.renovationSpend || 15000;

      // Cumulative Natural Appreciation
      const compoundedBaseValue = initialValue * Math.pow(1 + appreciationRate, years);
      
      // Renovation ROI: Typically home upgrades return ~65% value addition
      const renovationAddedValue = renovationSpend * 0.65;
      const finalProjectedValue = compoundedBaseValue + renovationAddedValue;
      
      const naturalAppreciationGain = compoundedBaseValue - initialValue;
      const totalNetGained = finalProjectedValue - initialValue - renovationSpend;

      const recommend = `In ${years} years, compounding at ${inputs.appreciationRate || 3.8}% annually, your home's total projected value is estimated to reach $${Math.round(finalProjectedValue).toLocaleString("en-US")}. Upgrade projects contribute an estimated $${Math.round(renovationAddedValue).toLocaleString("en-US")} in value addition.`;

      return {
        outputs: [
          { label: "Projected Future Home Value", value: finalProjectedValue, type: "currency", highlighted: true },
          { label: "Natural Appreciation Gain", value: naturalAppreciationGain, type: "currency" },
          { label: "Upgrade Added Value Return", value: renovationAddedValue, type: "currency" },
          { label: "Total Net Gained Net Worth", value: totalNetGained, type: "currency", highlighted: true }
        ],
        chartData: [
          { name: "Initial Asset Value", value: initialValue },
          { name: "Appreciation Gains", value: naturalAppreciationGain },
          { name: "Upgrade Contributions", value: renovationAddedValue },
          { name: "Total Invested Capital", value: initialValue + renovationSpend }
        ],
        recommendation: recommend
      };
    },
    guide: {
      overview: "Calculate the long-term wealth built through residential real estate compounding and strategic home remodeling.",
      howToUse: "Provide original cost/value, expected yearly appreciation, timeline, and optional renovation plans.",
      formulaExplanation: "Compounds the core price of the home annually and applies a normalized ROI multiplier to your renovation investments.",
      faq: [
        { question: "What is the average national home appreciation rate?", answer: "Historically, US residential properties average an appreciation rate of roughly 3.5% to 4.5% annually, closely tracking general price inflation." },
        { question: "Does remodeling pay back dollar-for-dollar?", answer: "Rarely. While renovations improve marketability and convenience, most remodels return only 55% to 75% of their cost in home value at resale." }
      ],
      seoContent800Words: "Placeholder SEO Content"
    }
  }
];
